import { useState } from 'react'
import { MagnifyingGlass, Plus, Tag } from '@phosphor-icons/react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Banner, Button, DataTable, EmptyState, Input, type Column } from '@/ui'
import { errorMessage } from '../api/core'
import { lookupCode, rollStatus, useRolls, type RollRow } from '../api/gate'
import { useVillages } from '../api/reference'
import { FilterBar, SelectFilter } from '../components/FilterBar'
import { ConsolePage, QueryState } from '../components/Layout'
import { RollActions } from '../components/RollActions'
import { Timeline } from '../components/Timeline'
import { Code, Facts, RefLink, Section, Status, UserName, When } from '../components/bits'
import { humanize } from '../labels'
import { useUrlState } from '../lib/useUrlState'
import { useScope } from '../api/appConfig'
import { ActionButton } from '../components/Can'

const LIMIT = 25

/** Rolls & tags (F4.1-i, F4.11-d, F4.6): who holds which roll, what is used, and a code lookup. */
export function RollsPage() {
  const navigate = useNavigate()
  const [f, setF, { reset, active }] = useUrlState({ kind: '', groupId: '', page: 1 })
  const villages = useVillages()
  const { v1 } = useScope()
  const q = useRolls({
    page: f.page,
    limit: LIMIT,
    kind: f.kind || undefined,
    groupId: f.groupId || undefined,
  })
  const columns: Column<RollRow>[] = [
    {
      id: 'kind',
      header: 'Kind',
      cell: (r) => (
        <Status
          status={r.kind}
          label={
            r.kind === 'SEAL' ? 'Seal strip' : r.kind === 'BAG' ? 'Bag tags' : humanize(r.kind)
          }
        />
      ),
    },
    {
      id: 'range',
      header: 'Codes',
      cell: (r) => (
        <Code>
          {r.villageCode ?? '—'} {r.startSequence ?? ''}–{r.endSequence ?? ''}
        </Code>
      ),
    },
    {
      id: 'holder',
      header: 'Issued to',
      cell: (r) =>
        r.issuedToId ? (
          <UserName id={r.issuedToId} />
        ) : (
          <span className="text-muted">In the office</span>
        ),
    },
    {
      id: 'use',
      header: 'Bound · void · unused',
      align: 'right',
      mono: true,
      cell: (r) => (
        <span>
          {r.bound} · {r.voided} ·{' '}
          <span className={r.unused > 0 ? 'text-ink' : 'text-muted'}>{r.unused}</span>
          <span className="text-muted"> / {r.count}</span>
        </span>
      ),
      hideBelow: 'sm',
    },
    {
      id: 'issued',
      header: 'Issued',
      cell: (r) => <When at={r.issuedAt} dateOnly />,
      hideBelow: 'lg',
    },
    { id: 'status', header: 'Status', cell: (r) => <Status status={rollStatus(r)} /> },
    {
      id: 'actions',
      header: <span className="sr-only">Actions</span>,
      cell: (r) => <RollActions roll={r} showAssign />,
      hideBelow: 'md',
    },
  ]
  return (
    <ConsolePage
      eyebrow="Farm gate"
      title="Rolls & tags"
      description={
        v1
          ? 'Printed tag rolls and seal strips: give each one to the village printed on it before the day; every phone of the village can use it. Look up any code to see what it is bound to.'
          : 'Printed tag rolls and seal strips: assign them to an operator before the day, take the return, void what is lost. Look up any code to see what it is bound to.'
      }
      actions={
        <ActionButton
          perm="qr-generation:create"
          hide
          leadingIcon={<Plus aria-hidden="true" />}
          onClick={() => navigate('/c/qr-generation')}
        >
          Generate codes
        </ActionButton>
      }
    >
      <CodeLookup />
      <DataTable<RollRow>
        caption="Rolls"
        columns={columns}
        rows={q.data?.rows ?? []}
        getRowId={(r) => r.id}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        pageSize={LIMIT}
        server={{ page: f.page, total: q.data?.total ?? 0, onPageChange: (page) => setF({ page }) }}
        filters={
          <FilterBar onClear={reset} active={active}>
            <SelectFilter
              label="Kind"
              value={f.kind}
              onChange={(kind) => setF({ kind })}
              options={['BAG', 'SEAL', 'LOCATION', 'MACHINE', 'OUTPUT_BAG', 'QC_SEAL'].map((k) => ({
                value: k,
                label: humanize(k),
              }))}
            />
            <SelectFilter
              label="Village"
              value={f.groupId}
              onChange={(groupId) => setF({ groupId })}
              options={(villages.data ?? []).map((v) => ({ value: v.id, label: v.name }))}
            />
          </FilterBar>
        }
        empty={
          <EmptyState
            icon={<Tag />}
            title="No rolls"
            description="Rolls are printed per village in QR generation (kind BAG or SEAL)."
          />
        }
      />
    </ConsolePage>
  )
}

function CodeLookup() {
  const [text, setText] = useState('')
  const [payload, setPayload] = useState('')
  const q = useQuery({
    queryKey: ['console', 'qr-code', payload],
    enabled: Boolean(payload),
    retry: false,
    queryFn: () => lookupCode(payload),
  })
  return (
    <Section title="Code status" eyebrow="Scan or type a tag, seal or label">
      <form
        className="flex flex-wrap gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          setPayload(text.trim())
        }}
      >
        <div className="min-w-[16rem] flex-1">
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="e.g. T-10448"
            aria-label="Code"
            leading={<MagnifyingGlass />}
            className="font-mono"
          />
        </div>
        <Button type="submit" variant="secondary" disabled={!text.trim()}>
          Look up
        </Button>
      </form>
      {payload && (
        <div className="mt-4">
          {q.isError ? (
            <Banner tone="warn">{errorMessage(q.error)}</Banner>
          ) : (
            <QueryState query={q} label="the code">
              {q.data && (
                <div className="flex flex-col gap-4">
                  <Facts
                    columns={4}
                    items={[
                      { label: 'Code', value: <Code>{q.data.payload}</Code> },
                      { label: 'Kind', value: humanize(q.data.kind) },
                      { label: 'Status', value: <Status status={q.data.status} /> },
                      { label: 'Holder', value: <UserName id={q.data.issuedToId} /> },
                      {
                        label: 'Bound to',
                        value: q.data.boundType
                          ? `${humanize(q.data.boundType)} · ${q.data.boundId?.slice(0, 8)}`
                          : '—',
                      },
                      { label: 'Bound', value: <When at={q.data.boundAt} /> },
                      { label: 'Void reason', value: q.data.voidReason ?? '—' },
                      {
                        label: 'Conflicts',
                        value: q.data.conflicts.length
                          ? q.data.conflicts.map((c) =>
                              c.unitId ? (
                                <RefLink key={c.id} to={`/c/lots/${c.unitId}`}>
                                  {humanize(c.type)}
                                </RefLink>
                              ) : (
                                humanize(c.type)
                              ),
                            )
                          : 'none',
                      },
                    ]}
                  />
                  <Timeline
                    items={q.data.diary.map((d) => ({
                      id: d.id,
                      at: d.occurredAt,
                      title: humanize(d.bizStep),
                      detail: (
                        <>
                          {humanize(d.subjectType)} · <UserName id={d.actorId} />
                          {d.sessionId && (
                            <>
                              {' '}
                              · <RefLink to={`/c/sessions/${d.sessionId}`}>day</RefLink>
                            </>
                          )}
                        </>
                      ),
                    }))}
                    empty="No diary lines for this code yet."
                  />
                </div>
              )}
            </QueryState>
          )}
        </div>
      )}
    </Section>
  )
}
